import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { CheckCircle2, AlertCircle } from 'lucide-react'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Checkbox } from '../ui/checkbox'
import { getNetlifyFunctionUrl, handleApiResponse } from '@/lib/api-helpers'

interface DownloadFormProps {
  content: {
    form: {
      nameLabel: string
      namePlaceholder: string
      emailLabel: string
      emailPlaceholder: string
      consentLabel: string
      submitButton: string
      submittingButton: string
    }
    messages: {
      success: string
      error: string
    }
  }
  downloadType: 'egta-2025' | 'egta-board-2025' | 'egta-2026' | 'future-of-media-2026'
}

interface FormData {
  name: string
  email: string
  consent: boolean
  // Honeypot field to catch bots
  company: string
}

export function DownloadForm({ content, downloadType }: DownloadFormProps) {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    consent: false,
    company: '', // Honeypot
  })

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<{
    type: 'success' | 'error' | null
    message: string
  }>({ type: null, message: '' })

  const [errors, setErrors] = useState({
    name: false,
    email: false,
    consent: false,
  })

  const validate = () => {
    const newErrors = {
      name: !formData.name.trim(),
      email: !formData.email || !/^\S+@\S+\.\S+$/.test(formData.email),
      consent: !formData.consent,
    }

    setErrors(newErrors)
    return !Object.values(newErrors).some(Boolean)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))

    // Clear error when field is edited
    if (errors[name as keyof typeof errors]) {
      setErrors((prev) => ({ ...prev, [name]: false }))
    }
  }

  const handleConsentChange = (checked: boolean) => {
    setFormData((prev) => ({ ...prev, consent: checked }))
    if (errors.consent) {
      setErrors((prev) => ({ ...prev, consent: false }))
    }
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    // Honeypot check - if filled, silently drop the submission
    if (formData.company) {
      console.log('Bot detected via honeypot')
      setSubmitStatus({
        type: 'success',
        message: content.messages.success,
      })
      return
    }

    if (!validate()) return

    setIsSubmitting(true)
    setSubmitStatus({ type: null, message: '' })

    try {
      const functionUrl = getNetlifyFunctionUrl('sendDownloadEmail')

      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          downloadType,
        }),
      })

      const result = await handleApiResponse(response)

      if (result.success) {
        setFormData({ name: '', email: '', consent: false, company: '' })
        setSubmitStatus({
          type: 'success',
          message: content.messages.success,
        })

        // Keep success message visible
        setTimeout(() => {
          setSubmitStatus({ type: null, message: '' })
        }, 8000)
      } else {
        setSubmitStatus({
          type: 'error',
          message: result.error || content.messages.error,
        })
      }
    } catch (err) {
      console.error('Error submitting form:', err)
      setSubmitStatus({
        type: 'error',
        message: content.messages.error,
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Honeypot field - hidden from users */}
      <div className="hidden" aria-hidden="true">
        <label htmlFor="company">Company (leave blank)</label>
        <input
          type="text"
          id="company"
          name="company"
          value={formData.company}
          onChange={handleChange}
          tabIndex={-1}
          autoComplete="off"
        />
      </div>

      <div>
        <label htmlFor="name" className="block text-sm font-medium mb-2">
          {content.form.nameLabel} <span className="text-red-500">*</span>
        </label>
        <Input
          id="name"
          name="name"
          placeholder={content.form.namePlaceholder}
          value={formData.name}
          onChange={handleChange}
          className={errors.name ? 'border-red-500' : ''}
        />
        {errors.name && (
          <p className="mt-1 text-sm text-red-500">Name is required</p>
        )}
      </div>

      <div>
        <label htmlFor="email" className="block text-sm font-medium mb-2">
          {content.form.emailLabel} <span className="text-red-500">*</span>
        </label>
        <Input
          id="email"
          name="email"
          type="email"
          placeholder={content.form.emailPlaceholder}
          value={formData.email}
          onChange={handleChange}
          className={errors.email ? 'border-red-500' : ''}
        />
        {errors.email && (
          <p className="mt-1 text-sm text-red-500">
            Please enter a valid email address
          </p>
        )}
      </div>

      <div className="flex items-start gap-3">
        <Checkbox
          id="consent"
          checked={formData.consent}
          onCheckedChange={handleConsentChange}
          className={errors.consent ? 'border-red-500' : ''}
        />
        <label
          htmlFor="consent"
          className="text-sm text-muted-foreground leading-relaxed cursor-pointer"
        >
          {content.form.consentLabel} <span className="text-red-500">*</span>
        </label>
      </div>
      {errors.consent && (
        <p className="text-sm text-red-500 -mt-3">
          You must agree to receive the materials
        </p>
      )}

      {submitStatus.message && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`flex items-start gap-2 p-4 rounded-md ${
            submitStatus.type === 'success'
              ? 'bg-green-100 text-green-800 dark:bg-green-800/20 dark:text-green-400'
              : 'bg-red-100 text-red-800 dark:bg-red-800/20 dark:text-red-400'
          }`}
        >
          {submitStatus.type === 'success' ? (
            <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" />
          ) : (
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          )}
          <p className="text-sm">{submitStatus.message}</p>
        </motion.div>
      )}

      <Button
        type="submit"
        disabled={isSubmitting}
        className="w-full"
        size="lg"
      >
        {isSubmitting
          ? content.form.submittingButton
          : content.form.submitButton}
      </Button>
    </form>
  )
}
